import SessionsIcon from "../../images/reports/sessionsIcon.svg?react";
import AssessmentsIcon from "../../images/reports/assessmentsIcon.svg?react";
import StudentsIcon from "../../images/reports/studentsIcon.svg?react";
import LiveIcon from "../../images/reports/liveIcon.svg?react";
import LiveHoursIcon from "../../images/reports/liveHoursIcon.svg?react";
import HoursIcon from "../../images/reports/hoursIcon.svg?react";
import SearchIcon from "../../images/reports/searchChartIcon.svg?react";
import NoChangeIcon from "../../images/reports/noChangeIcon.svg?react";
import IncreaseIcon from "../../images/reports/increaseIcon.svg?react";
import DecreaseIcon from "../../images/reports/decreaseIcon.svg?react";
import AverageHours from "../../images/reports/averageHours.svg?react";
import AverageSessions from "../../images/reports/averageSessions.svg?react";
import Flag from "../../images/reports/flaggedEventsIcon.svg?react";
import PlatformIcon from "../../images/reports/platformIcon.svg?react";
import AssessmentOverviewIcon from "../../images/reports/assessmentOverviewIcon.svg?react";
import { Circle } from "@mui/icons-material";
import { Box, Skeleton, Typography } from "@mui/material";
import { CoreCard, CoreCheckbox } from "@verity/components/core";
import { Chart, ChartEvent, ActiveElement, ChartDataset } from "chart.js";
import { useRef, useState, useEffect, useId } from "react";
import i18n from "../../i18n";
import { badgeStyle, NoResults } from "./report-components";
import { StatsCardsData } from "./report-types";
import styles from "../reports.module.css";

export const SummaryCard = (props: {
	title: string;
	number?: number;
	color: string;
	delta?: number;
	message: string;
	headerIcon?: React.ReactNode;
}) => {
	return (
		<CoreCard
			boxShadow
			style={{
				background: `radial-gradient(circle at 0% 0%, ${props.color}, white 20%)`,
			}}
		>
			<Box display="flex" flexDirection="column" height="100%">
				<div>
					<div style={{ marginBottom: "6px" }}>{props.headerIcon}</div>
					<div style={{ display: "flex", flexDirection: "row", gap: "6px" }}>
						<div style={{ display: "flex", flexDirection: "column", gap: "6px", marginRight: "auto" }}>
							<div className={styles.sectionCardTitle}>{props.title}</div>
							{props.number === undefined ? (
								<Skeleton width={"60px"}></Skeleton>
							) : (
								<div className={styles.sectionCardNumber}>{props.number}</div>
							)}
						</div>
						{props.delta !== undefined &&
							(props.delta > 0 ? <IncreaseIcon /> : props.delta < 0 ? <DecreaseIcon /> : <NoChangeIcon />)}
					</div>
				</div>
				<hr
					style={{
						border: "1.5px solid #EFF0F0",
						width: "100%",
						margin: "0px",
						marginTop: "6px",
						marginBottom: "7px",
					}}
				/>
				<div style={{ display: "flex", flexDirection: "row", gap: "10px", marginTop: "auto", marginBottom: "1px" }}>
					{props.delta !== undefined ? (
						<div
							className={[
								styles.changeNumber,
								props.delta > 0
									? styles.increaseNumber
									: props.delta < 0
										? styles.decreaseNumber
										: styles.noChangeNumber,
							].join(" ")}
						>
							{props.message}
						</div>
					) : (
						<Skeleton width="150px" />
					)}
				</div>
			</Box>
		</CoreCard>
	);
};

export const SummaryValueCard = (props: {
	title: string;
	number?: number;
	text?: string;
	color: string;
	headerIcon?: React.ReactNode;
}) => {
	return (
		<CoreCard
			boxShadow
			style={{
				background: `radial-gradient(circle at 0% 0%, ${props.color}, white 20%)`,
				minHeight: "93px",
			}}
		>
			<Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
				<div>
					<div style={{ marginBottom: "6px" }}>{props.headerIcon}</div>
					<div style={{ display: "flex", flexDirection: "row" }}>
						<div style={{ display: "flex", flexDirection: "column", gap: "6px", marginRight: "auto" }}>
							<div className={styles.sectionCardTitle}>{props.title}</div>
							{props.number === undefined && props.text === undefined ? (
								<Skeleton width={"60px"}></Skeleton>
							) : (
								<div className={styles.sectionCardNumber}>
									{props.number !== undefined ? props.number : props.text !== undefined && props.text}
								</div>
							)}
						</div>
					</div>
				</div>
			</Box>
		</CoreCard>
	);
};

export function StatisticsCards({
	assessments,
	students,
	veritySessions,
	liveSessions,
	proctoringHours,
	liveHours,
	averageSessions,
	averageHours,
	endDate,
	liveEnabled,
}: StatsCardsData & { endDate: Date; liveEnabled?: boolean }) {
	return (
		<Box
			sx={{
				display: "grid",
				gridTemplateColumns: {
					s: "repeat(1, 1fr)", // For extra small screens (1 column)
					md: "repeat(2, 1fr)", // For small screens (2 columns)
					lg: liveEnabled !== false ? "repeat(4, 1fr)" : "repeat(3, 1fr)", // For medium and larger screens (4 columns)
				},
				gridTemplateRows: "auto", // You can adjust rows similarly if needed
				columnGap: "30px",
				rowGap: "15px",
			}}
		>
			<SummaryCard
				title={i18n.t("instructor.components.reports.summaryCards.totalAssessmentsProctored")}
				number={assessments?.total}
				color="rgba(199, 40, 255, 15%)"
				headerIcon={<AssessmentsIcon />}
				delta={assessments?.month}
				message={i18n.t("instructor.components.reports.summaryCards.metricChange", {
					metric: `+${assessments?.month}`,
					date: endDate.toLocaleString(i18n.language, { month: "long", year: "numeric" }),
				})}
			/>
			{liveEnabled !== false && (
				<SummaryCard
					title={i18n.t("instructor.components.reports.summaryCards.totalLiveAttempts")}
					number={liveSessions?.total}
					color="rgba(136, 82, 210, 15%)"
					headerIcon={<LiveIcon />}
					delta={liveSessions?.month}
					message={i18n.t("instructor.components.reports.summaryCards.metricChange", {
						metric: `+${assessments?.month}`,
						date: endDate.toLocaleString(i18n.language, { month: "long", year: "numeric" }),
					})}
				/>
			)}
			<SummaryCard
				title={i18n.t("instructor.components.reports.summaryCards.totalAttempts")}
				number={veritySessions?.total}
				color="rgba(109, 161, 134, 15%)"
				headerIcon={<SessionsIcon />}
				delta={veritySessions?.month}
				message={i18n.t("instructor.components.reports.summaryCards.metricChange", {
					metric: `+${veritySessions?.month}`,
					date: endDate.toLocaleString(i18n.language, { month: "long", year: "numeric" }),
				})}
			/>
			<SummaryCard
				title={i18n.t("instructor.components.reports.summaryCards.avgAttemptsPerMonth")}
				number={averageSessions?.total && Math.round(averageSessions.total)}
				color="#BF7E1D15"
				headerIcon={<AverageSessions />}
				delta={averageSessions?.change !== undefined ? Math.round(averageSessions.change) : undefined}
				message={i18n.t("instructor.components.reports.summaryCards.metricChange", {
					metric: `${averageSessions?.change !== undefined && (Math.round(averageSessions.change) >= 0 ? "+" + Math.round(averageSessions.change) : Math.round(averageSessions?.change))}`,
					date: endDate.toLocaleString(i18n.language, { month: "long", year: "numeric" }),
				})}
			/>
			{liveEnabled !== false && (
				<SummaryCard
					title={i18n.t("instructor.components.reports.summaryCards.totalLiveHoursProctored")}
					number={liveHours?.total !== undefined ? Math.round(liveHours.total * 10) / 10 : undefined}
					color="rgba(112, 100, 183, 15%)"
					headerIcon={<LiveHoursIcon />}
					delta={liveHours?.month !== undefined ? Math.round(liveHours.month * 10) / 10 : undefined}
					message={i18n.t("instructor.components.reports.summaryCards.metricChange", {
						metric: `+${liveHours?.month && Math.round(liveHours.month * 10) / 10}`,
						date: endDate.toLocaleString(i18n.language, { month: "long", year: "numeric" }),
					})}
				/>
			)}
			<SummaryCard
				title={i18n.t("instructor.components.reports.summaryCards.totalUsersProctored")}
				number={students?.total}
				color="rgba(22, 120, 155, 15%)"
				headerIcon={<StudentsIcon />}
				delta={students?.month}
				message={i18n.t("instructor.components.reports.summaryCards.metricChange", {
					metric: `+${students?.month}`,
					date: endDate.toLocaleString(i18n.language, { month: "long", year: "numeric" }),
				})}
			/>
			<SummaryCard
				title={i18n.t("instructor.components.reports.summaryCards.totalHoursProctored")}
				number={proctoringHours?.total !== undefined ? Math.round(proctoringHours.total * 10) / 10 : undefined}
				color="rgba(31, 109, 212, 15%)"
				headerIcon={<HoursIcon />}
				delta={proctoringHours?.month !== undefined ? Math.round(proctoringHours.month * 10) / 10 : undefined}
				message={i18n.t("instructor.components.reports.summaryCards.metricChange", {
					metric: `+${proctoringHours?.month && Math.round(proctoringHours.month * 10) / 10}`,
					date: endDate.toLocaleString(i18n.language, { month: "long", year: "numeric" }),
				})}
			/>
			<SummaryCard
				title={i18n.t("instructor.components.reports.summaryCards.avgProctorHoursPerMonth")}
				number={averageHours?.total && Math.round(averageHours.total * 10) / 10}
				color="#E2726E15"
				headerIcon={<AverageHours />}
				delta={averageHours?.change !== undefined ? Math.round(averageHours.change * 10) / 10 : undefined}
				message={i18n.t("instructor.components.reports.summaryCards.metricChange", {
					metric: `${averageHours?.change !== undefined && (Math.round(averageHours.change * 10) / 10 >= 0 ? "+" + Math.round(averageHours.change * 10) / 10 : Math.round(averageHours?.change * 10) / 10)}`,
					date: endDate.toLocaleString(i18n.language, { month: "long", year: "numeric" }),
				})}
			/>
		</Box>
	);
}

/**
 * Screen reader text for the graph. This is so that users who uses screen readers can hear the data
 */
function ScreenReaderTableResults({
	monthLabels,
	sessionData,
	id,
}: {
	monthLabels: string[][];
	sessionData: {
		label: string;
		data?: number[];
		show: boolean;
	}[];
	id: string;
}) {
	// Only show sessions that are visible
	const visibleSessions = sessionData.filter((s) => s.show);
	const visibleLabels = visibleSessions.map((s) => s.label).join(", ");
	const methodLabel =
		visibleLabels.length === 0
			? i18n.t("instructor.components.reports.canvas.noMethod")
			: i18n.t("instructor.components.reports.canvas.srCanvasSubtitle", {
					methods: visibleLabels,
				});

	return (
		<div className={styles["sr-only"]} id={id}>
			<span>
				{i18n.t("instructor.components.reports.canvas.srCanvasTitle")}
				{methodLabel}
			</span>
			{/* Monthly breakdown */}
			{visibleSessions.length > 0 &&
				monthLabels.map((labelParts, i) => {
					const month = labelParts.join(" ");
					const isAllZero = (sessionData[0]?.data?.[i] ?? 0) === 0; // If "All Methods" is 0, skip narratoring the rest of the methods

					const breakdown = isAllZero
						? i18n.t("instructor.components.reports.canvas.zeroAttempts")
						: visibleSessions
								.map(({ label, data }) => {
									const val = data?.[i] ?? 0;
									return i18n.t("instructor.components.reports.canvas.attemptsForLabel", {
										count: val,
										label: label.toLowerCase(),
									});
								})
								.join(". ");

					return (
						<span key={i}>
							{i18n.t("instructor.components.reports.canvas.monthSummary", {
								month,
								breakdown,
							})}
						</span>
					);
				})}
		</div>
	);
}

export function ReportGraph(props: {
	totalVeritySessions?: number[];
	totalLiveSessions?: number[];
	totalLiveCheckInSessions?: number[];
	totalNonLiveSessions?: number[];
	startDateFilter?: Date | null;
	endDateFilter?: Date | null;
	type: "course" | "institution";
}) {
	const {
		totalVeritySessions,
		totalLiveSessions,
		totalLiveCheckInSessions,
		totalNonLiveSessions,
		startDateFilter,
		endDateFilter,
		type,
	} = props;

	const chartRef = useRef<HTMLCanvasElement>(null);
	const [chart, setChart] = useState<Chart | null>(null);
	const [showVerity, setShowVerity] = useState(true);
	const [showLive, setShowLive] = useState(true);
	const [showLiveCheckIn, setShowLiveCheckIn] = useState(true);
	const [showNonLive, setShowNonLive] = useState(true);
	const [monthLabels, setMonthLabels] = useState<string[][]>([]);
	const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

	const handleHover = (_event: ChartEvent, elements: ActiveElement[]) => {
		if (elements.length > 0) {
			const index = elements[0].index;
			setHoveredIndex(index);
		} else {
			setHoveredIndex(null);
		}
	};

	useEffect(() => {
		if (chartRef.current && !chart) {
			const ctx = chartRef.current.getContext("2d");
			if (ctx) {
				const newChart = new Chart(ctx, {
					type: "line",
					data: {
						labels: [],
						datasets: [],
					},
					options: {
						interaction: {
							mode: "index", // Highlight all datasets at the same index
							intersect: false, // Do not restrict to only the hovered point
						},
						onHover: handleHover,
						scales: {
							y: {
								beginAtZero: true,
								title: {
									display: true,
									text: i18n.t("instructor.components.reports.attempts"),
									color: "#000000",
									font: {
										weight: "400",
										size: 15,
									},
								},
								grid: {
									borderDash: [6, 6],
									borderColor: "rgba(196, 196, 196, 1)",
									color: "rgba(196, 196, 196, 1)",
									lineWidth: 1,
								},
							},
							x: {
								title: {
									display: true,
									text: i18n.t("instructor.components.reports.month"),
									color: "#000000",
									font: {
										weight: "400",
										size: 15,
									},
								},
								grid: {
									borderDash: [6, 6],
									borderColor: "rgba(196, 196, 196, 1)",
									lineWidth: 1,
								},
								ticks: {
									align: "start",
								},
							},
						},
						responsive: true,
						maintainAspectRatio: false,
						plugins: {
							legend: {
								display: false,
								labels: {
									font: {
										size: 15, // Same font size as axes
										weight: "200",
									},
									color: "#2B2F32", // Same font color as axes
								},
							},
							tooltip: {
								displayColors: false,
								callbacks: {
									title: (context) => {
										return context[0].label.split(",").join(" ");
									},
								},
								titleFont: {
									weight: "600",
								},
							},
							centerText: {
								text: i18n.t("instructor.components.reports.canvas.noMethod"),
							},
						},
						elements: {
							point: {
								borderWidth: 0,
								radius: 0.5,
								backgroundColor: "#DBDBDB",
							},
						},
					},
				});
				setChart(newChart);
			}
		}
	}, [chartRef, chart, showLive, showNonLive, showVerity, showLiveCheckIn]);

	useEffect(() => {
		if (startDateFilter && endDateFilter) {
			setMonthLabels(getMonthLabels(startDateFilter, endDateFilter));
		}
	}, [endDateFilter, startDateFilter]);

	useEffect(() => {
		if (chart && startDateFilter && endDateFilter) {
			chart.options.animation = { duration: 1000 };
			const datasets: ChartDataset[] = [
				{
					label: i18n.t("instructor.components.reports.canvas.allMethods"),
					data:
						totalVeritySessions?.length === 1
							? [totalVeritySessions[0], totalVeritySessions[0]]
							: (totalVeritySessions ?? []),
					borderColor: "#4F75CB",
					backgroundColor:
						showVerity && !showNonLive && !showLive && !showLiveCheckIn
							? "rgba(79, 117, 203, 10%)"
							: "rgba(0, 0, 0, 0%)",
					pointBackgroundColor: "#4F75CB",
					hidden: !showVerity,
					cubicInterpolationMode: "monotone",
					fill: true,
					pointHoverRadius: 5,
					pointHitRadius: 7,
					hoverBackgroundColor: "#4F75CB",
				},
				{
					label: i18n.t("instructor.components.reports.canvas.automatedLabel"),
					data:
						totalNonLiveSessions?.length === 1
							? [totalNonLiveSessions[0], totalNonLiveSessions[0]]
							: (totalNonLiveSessions ?? []),
					borderColor: "#E19278",
					backgroundColor:
						showNonLive && !showLive && !showLiveCheckIn && !showVerity
							? "rgba(225, 146, 120, 10%)"
							: "rgba(0, 0, 0, 0%)",
					pointBackgroundColor: "#E19278",
					hidden: !showNonLive,
					cubicInterpolationMode: "monotone",
					fill: true,
					pointHoverRadius: 5,
					pointHitRadius: 7,
					hoverBackgroundColor: "#E19278",
				},
				{
					label: i18n.t("instructor.components.reports.canvas.liveProctoringLabel"),
					data:
						totalLiveSessions?.length === 1 ? [totalLiveSessions[0], totalLiveSessions[0]] : (totalLiveSessions ?? []),
					borderColor: "#E992D8",
					backgroundColor:
						showLive && !showNonLive && !showVerity && !showLiveCheckIn
							? "rgba(233, 146, 216, 10%)"
							: "rgba(0, 0, 0, 0%)",
					pointBackgroundColor: "#E992D8",
					hidden: !showLive,
					cubicInterpolationMode: "monotone",
					fill: true,
					pointHoverRadius: 5,
					pointHitRadius: 7,
					hoverBackgroundColor: "#E992D8",
				},
				{
					label: i18n.t("instructor.components.reports.canvas.liveCheckInLabel"),
					data:
						totalLiveCheckInSessions?.length === 1
							? [totalLiveCheckInSessions[0], totalLiveCheckInSessions[0]]
							: (totalLiveCheckInSessions ?? []),
					borderColor: "#928FDC",
					backgroundColor:
						showLiveCheckIn && !showNonLive && !showLive && !showVerity
							? "rgba(146, 143, 220, 10%)"
							: "rgba(0, 0, 0, 0%)",
					pointBackgroundColor: "#928FDC",
					hidden: !showLiveCheckIn,
					cubicInterpolationMode: "monotone",
					fill: true,
					pointHoverRadius: 5,
					pointHitRadius: 7,
					hoverBackgroundColor: "#928FDC",
				},
			];
			if (monthLabels.length === 1) {
				chart.data.labels = [monthLabels[0], monthLabels[0]];
			} else {
				chart.data.labels = monthLabels;
			}

			chart.data.datasets = datasets;
			const displayCenterText = !showLive && !showNonLive && !showVerity && !showLiveCheckIn;
			if (!chart.options.plugins) {
				chart.options.plugins = {};
			}
			if (!chart.options.plugins.centerText) {
				chart.options.plugins.centerText = {};
			}
			chart.options.plugins.centerText.display = displayCenterText;
			chart.update();
		}
	}, [
		showVerity,
		showLive,
		showLiveCheckIn,
		showNonLive,
		chart,
		totalLiveCheckInSessions,
		totalLiveSessions,
		totalNonLiveSessions,
		totalVeritySessions,
		monthLabels,
	]);

	useEffect(() => {
		if (chart?.options?.scales?.x?.grid) {
			chart.options.animation = false;
			chart.options.scales.x.grid.color = (context) => {
				if (context.index === hoveredIndex) {
					return "rgba(100, 100, 100, 1)";
				} else {
					return "rgba(196, 196, 196, 1)";
				}
			};
			chart.update();
		}
	}, [hoveredIndex]);

	const getMonthLabels = (start: Date, end: Date) => {
		const labels: string[][] = [];
		const currentDate = new Date(start.getTime());
		while (currentDate <= end) {
			labels.push([`${currentDate.toLocaleString("default", { month: "short" })}`, `${currentDate.getFullYear()}`]);
			currentDate.setMonth(currentDate.getMonth() + 1);
		}
		return labels;
	};

	const sessionData = [
		{
			label: i18n.t("instructor.components.reports.canvas.allMethodsLabel"),
			data: totalVeritySessions,
			show: showVerity,
			setShow: setShowVerity,
			color: "#4F75CB",
		},
		{
			label: i18n.t("instructor.components.reports.canvas.automatedLabel"),
			data: totalNonLiveSessions,
			show: showNonLive,
			setShow: setShowNonLive,
			color: "#E19278",
		},
		{
			label: i18n.t("instructor.components.reports.canvas.liveProctoringLabel"),
			data: totalLiveSessions,
			show: showLive,
			setShow: setShowLive,
			color: "#E992D8",
		},
		{
			label: i18n.t("instructor.components.reports.canvas.liveCheckInLabel"),
			data: totalLiveCheckInSessions,
			show: showLiveCheckIn,
			setShow: setShowLiveCheckIn,
			color: "#928FDC",
		},
	];

	const canvasId = useId();

	return (
		<CoreCard
			style={{ width: "100%", overflow: "auto" }}
			useHeader
			title={i18n.t("instructor.components.reports.canvas.title")}
			icon={SearchIcon}
		>
			<div style={{ display: "flex", width: "100%", flexDirection: "column", gap: "20px", minWidth: "300px" }}>
				<div
					style={{
						fontSize: "15px",
						fontWeight: 400,
						textAlign: "left",
						color: "#6B6B6B",
						marginTop: "10px",
					}}
				>
					{i18n.t("instructor.components.reports.canvas.description", {
						type: type,
					})}
				</div>
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: {
							m: "1fr", // For small screens (1 column, 1x2 layout)
							lg: "28% 72%", // For medium and larger screens (2 columns, 2x1 layout)
						},
						gridTemplateRows: "auto",
						alignItems: "center",
						gap: "10px",
						marginX: { lg: "20px", md: "0px" },
						minWidth: "300px",
					}}
				>
					<div style={{ display: "flex", flexDirection: "column", gap: "20px", marginRight: "24px" }}>
						{sessionData.map(({ label, data, show, setShow, color }, index) => {
							const labelId = `${index}-${canvasId}-checkbox-label`;
							const badgeId = `${index}-${canvasId}-badge-label`;
							const badgeCount = data?.reduce((acc, val) => acc + val, 0) ?? 0;

							return (
								<div key={label}>
									<div
										style={{
											display: "flex",
											flexDirection: "row",
											gap: "5px",
											justifyContent: "start",
											alignItems: "center",
										}}
									>
										<CoreCheckbox
											checked={show}
											onChange={() => setShow(!show)}
											sx={{ padding: "2px" }}
											size={"15px"}
											inputProps={{
												"aria-labelledby": `${labelId} ${badgeId}`,
											}}
										/>
										<Circle sx={{ color, width: "12px" }} />
										<div
											style={{
												fontSize: "15px",
												fontWeight: 500,
												textAlign: "left",
												color: "#2B2F32",
											}}
											id={labelId}
										>
											{label}
										</div>
										<div style={{ ...badgeStyle, backgroundColor: `${color}40` }} id={badgeId}>
											<span className={styles["sr-only"]}>
												{i18n.t("instructor.components.reports.canvas.numAssessment", {
													count: badgeCount,
												})}
											</span>
											<span aria-hidden={true}>{data ? badgeCount : <Skeleton width={"32px"} height={"16px"} />}</span>
										</div>
									</div>
									{index === 0 && (
										<hr
											style={{
												lineHeight: "0.5",
												width: "100%",
												backgroundColor: "#858687",
												margin: "0",
												marginTop: "20px",
											}}
										/>
									)}
								</div>
							);
						})}
					</div>
					<Box
						onMouseLeave={() => setHoveredIndex(null)}
						role="region"
						tabIndex={0}
						aria-describedby={canvasId}
						sx={{
							height: "400px",
							position: "relative",
							minWidth: "300px",
							borderRadius: "4px",
							"&:focus-visible": {
								outline: "2px solid var(--focus-outline-color-dark)",
								outlineOffset: "2px",
							},
						}}
					>
						<canvas
							role="presentation"
							ref={chartRef}
							style={{ width: "100%", display: totalLiveCheckInSessions ? "" : "none" }}
						/>
						<ScreenReaderTableResults monthLabels={monthLabels} sessionData={sessionData} id={canvasId} />
						{totalVeritySessions === undefined && (
							<Box display="flex" width="100%" height="100%" alignItems="center" justifyContent="center">
								<Skeleton variant="rounded" width={"90%"} height={"90%"} />
							</Box>
						)}
					</Box>
				</Box>
			</div>
		</CoreCard>
	);
}

function CustomDoughnutChart({
	title,
	labels,
	data,
	backgroundColors,
	centerText,
	secondaryText,
	labelText,
	semiCircle,
	smallText,
}: {
	title: string;
	labels: string[];
	data: number[];
	backgroundColors: string[];
	centerText: string;
	secondaryText?: string;
	labelText?: string;
	semiCircle?: boolean;
	smallText?: boolean;
}) {
	const chartRef = useRef<HTMLCanvasElement>(null);
	const chartInstanceRef = useRef<Chart<"doughnut", number[], string> | null>(null);
	const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

	// Filter out items with data value of 0
	const filteredData = data.filter((value) => value !== 0);
	const filteredLabels = labels.filter((_, index) => data[index] !== 0);
	const filteredBackgroundColors = backgroundColors.filter((_, index) => data[index] !== 0);

	const handleHover = (_event: ChartEvent, elements: ActiveElement[]) => {
		if (elements.length > 0) {
			const index = elements[0].index;
			setHoveredIndex(index);
		} else {
			setHoveredIndex(null);
		}
	};

	useEffect(() => {
		if (chartRef.current) {
			const ctx = chartRef.current.getContext("2d");

			if (ctx) {
				const sum = filteredData.reduce((sum, v) => sum + v, 0);

				// Destroy the previous chart instance if it exists
				if (chartInstanceRef.current) {
					chartInstanceRef.current.destroy();
				}

				// Create the chart instance
				chartInstanceRef.current = new Chart(ctx, {
					type: "doughnut",
					data: {
						labels: filteredLabels,
						datasets: [
							{
								label: title,
								data: filteredData,
								backgroundColor: filteredBackgroundColors,
							},
						],
					},
					options: {
						responsive: true,
						cutout: "70%",
						onHover: handleHover,
						...(semiCircle && { rotation: -90, circumference: 180 }),
						plugins: {
							legend: {
								display: false, // Disable default legend
							},
							centerText: {
								display: true,
								text: `${centerText}\n${secondaryText ? secondaryText : sum}`,
								semiCircle: semiCircle,
								smallText: smallText,
							},
							tooltip: {
								displayColors: false,
								titleFont: {
									weight: "600",
								},
								bodyFont: {
									weight: "200",
								},
							},
						},
					},
				});
			}
		}
	}, []);

	return (
		<Box
			minHeight={semiCircle ? "180px" : "210px"}
			paddingY={semiCircle ? "0px" : "40px"}
			sx={{
				display: "grid",
				gridTemplateColumns: labelText
					? {
							md: "repeat(1, 1fr)",
							lg: "1fr 1fr",
						}
					: "5fr 3fr",
				gridTemplateRows: "auto",
				columnGap: "min(120px, 20%)",
				rowGap: "2px",
				alignItems: "center",
			}}
		>
			<Box
				sx={{
					display: "flex",
					justifyContent: labelText
						? {
								md: "flex-start",
								lg: "flex-end",
							}
						: "flex-end",
				}}
			>
				<div style={{ maxWidth: "210px", maxHeight: semiCircle ? "140px" : "210px" }}>
					<canvas
						ref={chartRef}
						style={{ maxHeight: semiCircle ? "140px" : "210px", maxWidth: "210px", minWidth: "210px" }}
						onMouseLeave={() => setHoveredIndex(null)}
					/>
				</div>
			</Box>

			{/* Custom Legend */}
			<Box
				display="flex"
				flexDirection="column"
				gap="10px"
				paddingRight="8px"
				onMouseEnter={() => setHoveredIndex(null)}
			>
				{filteredLabels.map((label, index) => (
					<Box key={index} display="flex" alignItems="center" gap="10px">
						<Circle
							sx={{
								width: "15px",
								height: "15px",
								color: filteredBackgroundColors[index],
							}}
						/>
						<Typography fontWeight={hoveredIndex === index ? 700 : 500} fontSize="14px" lineHeight={1.6}>
							{label +
								(labelText ? ` - ${filteredData[index]} ${labelText + (filteredData[index] > 1 ? "s" : "")}` : "")}
						</Typography>
					</Box>
				))}
			</Box>
		</Box>
	);
}

export function SeverityChart({
	severityCounts,
}: {
	severityCounts?: { none: number; minor: number; major: number; severe: number };
}) {
	const labels = [
		i18n.t("instructor.components.reports.severe"),
		i18n.t("instructor.components.reports.major"),
		i18n.t("instructor.components.reports.minor"),
		i18n.t("instructor.components.reports.noFlags"),
	];
	const backgroundColors = ["#E2726E", "#EBBE79", "#6DA186", "#E6E6E6"];
	const data = [
		severityCounts?.severe ?? 0,
		severityCounts?.major ?? 0,
		severityCounts?.minor ?? 0,
		severityCounts?.none ?? 0,
	];

	return (
		<CoreCard
			icon={Flag}
			iconStroke={true}
			useHeader
			title={i18n.t("instructor.components.reports.severityChart.title")}
			style={{ overflow: "auto", minHeight: "350px" }}
		>
			{severityCounts ? (
				data.reduce((sum, value) => sum + value, 0) > 0 ? (
					<>
						<div
							style={{
								fontSize: "15px",
								fontWeight: 400,
								textAlign: "left",
								color: "#6B6B6B",
								marginTop: "10px",
								marginBottom: "20px",
							}}
						>
							{i18n.t("instructor.components.reports.severityChart.viewHighestFlag")}
						</div>
						<CustomDoughnutChart
							title={i18n.t("instructor.components.reports.severityChart.severityLevels")}
							labels={labels}
							data={data}
							backgroundColors={backgroundColors}
							centerText="Total"
						/>
					</>
				) : (
					<NoResults text={i18n.t("instructor.components.reports.severityChart.noSeverityData")} />
				)
			) : (
				<Box display="flex" justifyContent="center" alignItems="center" height="100%" width="100%">
					<Skeleton variant="rounded" width="90%" height="80%" />
				</Box>
			)}
		</CoreCard>
	);
}

export function AssessmentCompletionChart({
	completionCounts,
}: {
	completionCounts?: { complete: number; incomplete: number; unavailable: number; pending: number; error: number };
}) {
	const labels = [
		i18n.t("instructor.components.reports.completionChart.complete"),
		i18n.t("instructor.components.reports.completionChart.incomplete"),
		i18n.t("instructor.components.reports.completionChart.unavailable"),
		i18n.t("instructor.components.reports.completionChart.pending"),
		i18n.t("instructor.components.reports.completionChart.error"),
	];
	const backgroundColors = ["#81CCC9", "#E992D8", "#E19278", "#928FDC", "#939393"];
	const data = [
		completionCounts?.complete ?? 0,
		completionCounts?.incomplete ?? 0,
		completionCounts?.unavailable ?? 0,
		completionCounts?.pending ?? 0,
		completionCounts?.error ?? 0,
	];

	return (
		<CoreCard
			icon={AssessmentOverviewIcon}
			iconStroke={true}
			useHeader
			title={i18n.t("instructor.components.reports.completionChart.title")}
			style={{ overflow: "auto", minHeight: "200px", width: "100%" }}
		>
			{completionCounts ? (
				data.reduce((sum, value) => sum + value, 0) > 0 ? (
					<>
						<div
							style={{
								fontSize: "15px",
								fontWeight: 400,
								textAlign: "left",
								color: "#6B6B6B",
							}}
						>
							{i18n.t("instructor.components.reports.completionChart.viewProgress")}
						</div>
						<CustomDoughnutChart
							title={i18n.t("instructor.components.reports.completionChart.completionLevels")}
							labels={labels}
							data={data}
							backgroundColors={backgroundColors}
							centerText="Progress"
							secondaryText="Overview"
							labelText={i18n.t("instructor.components.reports.testTaker")}
							semiCircle
							smallText
						/>
					</>
				) : (
					<NoResults text={i18n.t("instructor.components.reports.completionChart.noCompletionData")} />
				)
			) : (
				<Box display="flex" justifyContent="center" alignItems="center" height="100%" width="100%">
					<Skeleton variant="rounded" width="90%" height="80%" />
				</Box>
			)}
		</CoreCard>
	);
}

export function AssessmentSeverityChart({
	severityCounts,
}: {
	severityCounts?: { none: number; minor: number; major: number; severe: number };
}) {
	const labels = [
		i18n.t("instructor.components.reports.severe"),
		i18n.t("instructor.components.reports.major"),
		i18n.t("instructor.components.reports.minor"),
		i18n.t("instructor.components.reports.noFlags"),
	];
	const backgroundColors = ["#E2726E", "#EBBE79", "#6DA186", "#E6E6E6"];
	const data = [
		severityCounts?.severe ?? 0,
		severityCounts?.major ?? 0,
		severityCounts?.minor ?? 0,
		severityCounts?.none ?? 0,
	];

	return (
		<CoreCard
			icon={Flag}
			iconStroke={true}
			useHeader
			title={i18n.t("instructor.components.reports.title")}
			style={{ overflow: "auto", minHeight: "200px", width: "100%" }}
		>
			{severityCounts ? (
				data.reduce((sum, value) => sum + value, 0) > 0 ? (
					<>
						<div
							style={{
								fontSize: "15px",
								fontWeight: 400,
								textAlign: "left",
								color: "#6B6B6B",
							}}
						>
							{i18n.t("instructor.components.reports.description")}
						</div>
						<CustomDoughnutChart
							title={i18n.t("instructor.components.reports.severityLevel")}
							labels={labels}
							data={data}
							backgroundColors={backgroundColors}
							centerText="Flag"
							secondaryText="Overview"
							labelText={i18n.t("instructor.components.reports.testTaker")}
							semiCircle
							smallText
						/>
					</>
				) : (
					<NoResults text={i18n.t("instructor.components.reports.noSeverityData")} />
				)
			) : (
				<Box display="flex" justifyContent="center" alignItems="center" height="100%" width="100%">
					<Skeleton variant="rounded" width="90%" height="80%" />
				</Box>
			)}
		</CoreCard>
	);
}

export function PlatformChart({
	platformCounts,
}: {
	platformCounts?: { lockdownCount: number; mobileCount: number; extensionCount: number };
}) {
	const labels = [
		i18n.t("portal.components.assessmentPageTSX.columnList.lockdown"),
		i18n.t("portal.components.assessmentPageTSX.columnList.mobileAndLockdown"),
		i18n.t("portal.components.assessmentPageTSX.columnList.extension"),
	];
	const backgroundColors = ["rgba(225, 146, 120, 1)", "rgba(235, 190, 121, 1)", "rgba(233, 146, 216, 1)"];
	const data = [
		platformCounts?.lockdownCount ?? 0,
		platformCounts?.mobileCount ?? 0,
		platformCounts?.extensionCount ?? 0,
	];

	return (
		<CoreCard
			icon={PlatformIcon}
			useHeader
			title={i18n.t("portal.components.assessmentPageTSX.columnList.usage")}
			style={{ overflow: "auto", minHeight: "350px" }}
		>
			{platformCounts ? (
				data.reduce((sum, value) => sum + value, 0) > 0 ? (
					<>
						<div
							style={{
								fontSize: "15px",
								fontWeight: 400,
								textAlign: "left",
								color: "#6B6B6B",
								marginTop: "10px",
								marginBottom: "20px",
							}}
						>
							{i18n.t("portal.components.assessmentPageTSX.columnList.description")}
						</div>
						<CustomDoughnutChart
							title={i18n.t("portal.components.assessmentPageTSX.columnList.types")}
							labels={labels}
							data={data}
							backgroundColors={backgroundColors}
							centerText="Total"
						/>
					</>
				) : (
					<NoResults text={i18n.t("portal.components.assessmentPageTSX.columnList.noPlatformData")} />
				)
			) : (
				<Box display="flex" justifyContent="center" alignItems="center" height="100%" width="100%">
					<Skeleton variant="rounded" width="90%" height="80%" />
				</Box>
			)}
		</CoreCard>
	);
}
